import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangleIcon, RefreshCwIcon, WifiOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { toApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  title?: string;
  description?: string;
  /** Raw error; rendered in a <details> so support can read it without a console. */
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}

/** Inline error panel. Every list and detail screen renders this on failure. */
export function ErrorState({ title, description, error, onRetry, className }: ErrorStateProps) {
  const { t } = useTranslation();
  const apiError = error === undefined ? null : toApiError(error);
  const offline = apiError?.status === 0;
  const Icon = offline ? WifiOffIcon : AlertTriangleIcon;

  const heading = title ?? (offline ? t("error.networkTitle") : t("error.title"));
  const body =
    description ?? apiError?.detail ?? (offline ? t("error.networkBody") : t("error.body"));

  return (
    <div
      role="alert"
      className={cn(
        "border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-xl border px-6 py-10 text-center",
        className,
      )}
    >
      <div
        className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full"
        aria-hidden="true"
      >
        <Icon className="size-6" />
      </div>
      <h3 className="text-lg font-semibold">{heading}</h3>
      <p className="text-muted-foreground max-w-md text-sm text-balance">{body}</p>
      {apiError?.code ? (
        <details className="text-muted-foreground max-w-full text-xs">
          <summary className="cursor-pointer">{t("error.detail")}</summary>
          <code className="mt-1 block break-all">
            {apiError.status} · {apiError.code}
          </code>
        </details>
      ) : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
          <RefreshCwIcon />
          {t("action.retry")}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * `errorElement` for the router: renders a full page rather than an inline panel,
 * because at this level the layout itself has failed.
 */
export function RouteError() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const error = useRouteError();

  const description = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : undefined;

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-md space-y-4">
        <ErrorState title={t("error.title")} description={description ?? t("error.body")} />
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>
            {t("action.goBack")}
          </Button>
          <Button onClick={() => window.location.assign("/")}>{t("action.goHome")}</Button>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Last line of defence around the whole app.
 *
 * A render-time crash in one widget should not leave a white page — the user
 * gets a readable message and a way back.
 */
export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept as console.error deliberately: this is the one place a stack trace is
    // worth more than a toast, and Sentry (Phase 2) hooks in right here.
    console.error("Unhandled UI error", error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4">
          <ErrorState error={this.state.error} />
          <div className="flex justify-center">
            <Button onClick={this.handleReset}>Go to dashboard</Button>
          </div>
        </div>
      </div>
    );
  }
}
